import type { PlatformId } from "@/lib/types/platform"

const PLATFORM_ALIASES: Record<string, PlatformId> = {
  webflow: "webflow",
  framer: "framer",
  wordpress: "wordpress",
  wp: "wordpress",
  nextjs: "nextjs",
  "next.js": "nextjs",
  next: "nextjs",
  shopify: "shopify",
  squarespace: "squarespace",
  wix: "wix",
  bubble: "bubble",
  ghost: "ghost",
  "ghost-cms": "ghost",
  ghostcms: "ghost",
  kajabi: "kajabi",
  podia: "podia",
  lovable: "lovable",
  "lovable.dev": "lovable",
  bolt: "bolt",
  "bolt.new": "bolt",
  boltnew: "bolt",
  astro: "astro",
  beehiiv: "beehiiv",
  beehive: "beehiiv",
  kit: "kit",
  convertkit: "kit",
  "convert-kit": "kit",
  vuejs: "vuejs",
  vue: "vuejs",
  "vue.js": "vuejs",
  replit: "replit",
  django: "django",
  laravel: "laravel",
  reactrouter: "reactrouter",
  "react-router": "reactrouter",
  "react router": "reactrouter",
  v0: "v0",
  "vercel-v0": "v0",
  vercelv0: "v0",
  generic: "generic",
  other: "generic",
  manual: "generic",
  unknown: "generic",
}

export type PlatformInference = {
  platform: PlatformId
  confidence: number
  signals: string[]
}

/** Normalize any backend / user string into a known PlatformId. */
export function normalizePlatformId(value: unknown): PlatformId | null {
  if (typeof value !== "string") return null
  const key = value.trim().toLowerCase().replace(/\s+/g, "")
  return PLATFORM_ALIASES[key] ?? PLATFORM_ALIASES[value.trim().toLowerCase()] ?? null
}

/**
 * Fast domain-based heuristics for instant UI before Firecrawl returns.
 * Prefer hosted suffixes (high confidence). Loose brand substrings stay
 * medium so HTML/Firecrawl can still override false positives.
 */
export function inferPlatformFromDomain(domain: string): PlatformInference {
  const d = domain
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "")
    .replace(/^www\./, "")
    .trim()
  const signals: string[] = []

  if (!d) return { platform: "generic", confidence: 0, signals }

  const hosted = (
    platform: PlatformId,
    confidence: number,
    signal: string,
  ): PlatformInference => {
    signals.push(signal)
    return { platform, confidence, signals }
  }

  // —— High-confidence hosted suffixes ——
  if (d.endsWith(".webflow.io") || d.endsWith(".webflow.com")) {
    return hosted("webflow", 0.95, "domain:webflow-hosted")
  }
  if (
    d.endsWith(".framer.website") ||
    d.endsWith(".framer.app") ||
    d.endsWith(".framercanvas.com") ||
    d.endsWith(".framer.media")
  ) {
    return hosted("framer", 0.95, "domain:framer-hosted")
  }
  if (d.endsWith(".myshopify.com")) {
    return hosted("shopify", 0.95, "domain:shopify-hosted")
  }
  if (d.endsWith(".wordpress.com") || d.endsWith(".wp.com")) {
    return hosted("wordpress", 0.9, "domain:wordpress-hosted")
  }
  if (d.endsWith(".squarespace.com") || d.endsWith(".sqsp.net")) {
    return hosted("squarespace", 0.95, "domain:squarespace-hosted")
  }
  if (d.endsWith(".wixsite.com") || d.endsWith(".wix.com")) {
    return hosted("wix", 0.95, "domain:wix-hosted")
  }
  if (d.endsWith(".bubbleapps.io") || d.endsWith(".bubble.is")) {
    return hosted("bubble", 0.95, "domain:bubble-hosted")
  }
  if (d.endsWith(".ghost.io")) {
    return hosted("ghost", 0.9, "domain:ghost-hosted")
  }
  if (d.endsWith(".mykajabi.com") || d.endsWith(".kajabi.com")) {
    return hosted("kajabi", 0.95, "domain:kajabi-hosted")
  }
  if (d.endsWith(".podia.com") || d.endsWith(".podia.page")) {
    return hosted("podia", 0.95, "domain:podia-hosted")
  }
  if (d.endsWith(".lovable.app") || d.endsWith(".lovable.dev") || d.endsWith(".gptengineer.app")) {
    return hosted("lovable", 0.95, "domain:lovable-hosted")
  }
  if (d.endsWith(".bolt.host") || d.endsWith(".bolt.new") || d === "bolt.new") {
    return hosted("bolt", 0.9, "domain:bolt-hosted")
  }
  if (d.endsWith(".beehiiv.com")) {
    return hosted("beehiiv", 0.95, "domain:beehiiv-hosted")
  }
  if (d.endsWith(".kit.com") || d.endsWith(".convertkit.com") || d.endsWith(".ck.page")) {
    return hosted("kit", 0.95, "domain:kit-hosted")
  }
  if (d.endsWith(".replit.app") || d.endsWith(".repl.co")) {
    return hosted("replit", 0.9, "domain:replit-hosted")
  }
  if (d.endsWith(".vusercontent.net") || d.endsWith(".v0.dev")) {
    return hosted("v0", 0.85, "domain:v0-hosted")
  }
  if (d.endsWith(".astro.build")) {
    return hosted("astro", 0.7, "domain:astro-hosted")
  }

  // —— Medium: brand in hostname (custom vanity still needs HTML confirm) ——
  if (/\bwebflow\b/.test(d) || d.includes(".webflow.")) {
    return hosted("webflow", 0.65, "domain:webflow-name")
  }
  if (/\bframer\b/.test(d)) {
    return hosted("framer", 0.65, "domain:framer-name")
  }
  if (d.includes("myshopify") || /\bshopify\b/.test(d)) {
    return hosted("shopify", 0.6, "domain:shopify-name")
  }
  if (/\bwordpress\b/.test(d) || /(^|\.)wp\./.test(d)) {
    return hosted("wordpress", 0.55, "domain:wordpress-name")
  }
  if (/\bsquarespace\b/.test(d)) {
    return hosted("squarespace", 0.65, "domain:squarespace-name")
  }
  // Avoid bare "wix" false positives inside unrelated hosts (e.g. "rewix…")
  if (/(^|\.)wix[.-]|wixsite/.test(d)) {
    return hosted("wix", 0.6, "domain:wix-name")
  }
  if (/\bbubbleapps\b/.test(d) || /\bbubble\.is\b/.test(d)) {
    return hosted("bubble", 0.65, "domain:bubble-name")
  }
  if (/\bghost\.io\b/.test(d) || /(^|\.)ghost\./.test(d)) {
    return hosted("ghost", 0.55, "domain:ghost-name")
  }
  if (/\bmykajabi\b/.test(d) || /\bkajabi\b/.test(d)) {
    return hosted("kajabi", 0.65, "domain:kajabi-name")
  }
  if (/\bpodia\b/.test(d)) {
    return hosted("podia", 0.65, "domain:podia-name")
  }
  if (d.includes("lovable.app") || d.includes("lovable.dev") || d.includes("gptengineer")) {
    return hosted("lovable", 0.7, "domain:lovable-name")
  }
  if (d.includes("bolt.new") || /\.bolt\./.test(d)) {
    return hosted("bolt", 0.7, "domain:bolt-name")
  }
  if (/\bbeehiiv\b/.test(d)) {
    return hosted("beehiiv", 0.7, "domain:beehiiv-name")
  }
  if (/\bconvertkit\b/.test(d) || /(^|\.)kit\.com$/.test(d)) {
    return hosted("kit", 0.7, "domain:kit-name")
  }
  if (/\breplit\b/.test(d)) {
    return hosted("replit", 0.55, "domain:replit-name")
  }
  if (d.includes("v0.dev")) {
    return hosted("v0", 0.7, "domain:v0-name")
  }

  // Hosting hints — weak; HTML/Firecrawl should upgrade.
  if (d.endsWith(".vercel.app") || d.endsWith(".netlify.app") || d.endsWith(".netlify.com")) {
    return hosted("nextjs", 0.3, "domain:vercel-or-netlify")
  }
  if (d.endsWith(".pages.dev") || d.endsWith(".workers.dev")) {
    return hosted("generic", 0.15, "domain:cloudflare-pages")
  }

  return { platform: "generic", confidence: 0.1, signals }
}

type HtmlSignalRule = {
  platform: PlatformId
  confidence: number
  id: string
  test: (haystack: string) => boolean
}

/**
 * Fingerprints for raw HTML and Jina-style markdown (CDN hosts usually survive).
 * Confidence is per-rule; multiple matches for the same platform get a boost.
 */
const HTML_SIGNAL_RULES: HtmlSignalRule[] = [
  {
    platform: "webflow",
    confidence: 0.96,
    id: "meta:generator=Webflow",
    test: (h) =>
      /name=["']generator["'][^>]*content=["'][^"']*webflow/i.test(h) ||
      /content=["'][^"']*webflow["'][^>]*name=["']generator["']/i.test(h) ||
      /generator["']?\s*:\s*["']?webflow/i.test(h),
  },
  {
    platform: "webflow",
    confidence: 0.94,
    id: "attr:data-wf-site",
    test: (h) => /data-wf-(?:site|domain|page)=/i.test(h),
  },
  {
    platform: "webflow",
    confidence: 0.92,
    id: "cdn:website-files",
    test: (h) =>
      /cdn\.prod\.website-files\.com|assets\.website-files\.com|uploads-ssl\.webflow\.com|proxy-ssl\.webflow\.com/i.test(
        h,
      ),
  },
  {
    platform: "framer",
    confidence: 0.96,
    id: "meta:generator=Framer",
    test: (h) =>
      /name=["']generator["'][^>]*content=["'][^"']*framer/i.test(h) ||
      /content=["'][^"']*framer["'][^>]*name=["']generator["']/i.test(h) ||
      /Made in Framer/i.test(h),
  },
  {
    platform: "framer",
    confidence: 0.93,
    id: "cdn:framerusercontent",
    test: (h) => /framerusercontent\.com|__framer|framer\.com\/m\//i.test(h),
  },
  {
    platform: "shopify",
    confidence: 0.95,
    id: "cdn:shopify",
    test: (h) =>
      /cdn\.shopify\.com|Shopify\.theme|myshopify\.com|shopifycloud\.com|cdn\.shopifycdn\.net/i.test(
        h,
      ),
  },
  {
    platform: "shopify",
    confidence: 0.9,
    id: "meta:shopify",
    test: (h) =>
      /shopify-digital-wallet|Shopify\.Analytics|window\.Shopify\s*=/i.test(h),
  },
  {
    platform: "wordpress",
    confidence: 0.92,
    id: "path:wp-content",
    test: (h) =>
      /wp-content\/|wp-includes\/|wp-json\/|name=["']generator["'][^>]*WordPress/i.test(h),
  },
  {
    platform: "wordpress",
    confidence: 0.85,
    id: "header:wp",
    test: (h) => /x-powered-by:\s*wordpress|link:\s*<[^>]*wp-json/i.test(h),
  },
  {
    platform: "wix",
    confidence: 0.93,
    id: "cdn:wix",
    test: (h) =>
      /static\.wixstatic\.com|X-Wix-|wix\.com\/_api|wix-thunderbolt|parastorage\.com/i.test(h),
  },
  {
    platform: "squarespace",
    confidence: 0.93,
    id: "cdn:squarespace",
    test: (h) =>
      /squarespace-cdn\.com|static\.squarespace\.com|squarespace\.com\/static|sqsp\.net/i.test(
        h,
      ),
  },
  {
    platform: "bubble",
    confidence: 0.94,
    id: "cdn:bubble",
    test: (h) =>
      /cdn\.bubble\.io|meta-q\.cdn\.bubble\.io|bubbleapps\.io|bubble\.io\/package|\/package\/bubble/i.test(
        h,
      ),
  },
  {
    platform: "ghost",
    confidence: 0.93,
    id: "meta:generator=Ghost",
    test: (h) =>
      /name=["']generator["'][^>]*content=["'][^"']*Ghost/i.test(h) ||
      /content=["'][^"']*Ghost["'][^>]*name=["']generator["']/i.test(h) ||
      /ghost\.org\/|ghost-url|content=["']Ghost\s/i.test(h),
  },
  {
    platform: "kajabi",
    confidence: 0.94,
    id: "cdn:kajabi",
    test: (h) =>
      /kajabi-storefronts|kajabi-cdn|mykajabi\.com|kajabi-products|cdn\.kajabi\.com/i.test(h),
  },
  {
    platform: "podia",
    confidence: 0.93,
    id: "cdn:podia",
    test: (h) =>
      /cdn\.podia\.com|cdn\.sanity\.io\/images\/f6u8j0m2|data-podia|podia\.com\/assets/i.test(
        h,
      ),
  },
  {
    platform: "lovable",
    confidence: 0.9,
    id: "marker:lovable",
    test: (h) => /lovable\.dev|lovable\.app|gptengineer\.app|lovable\.project/i.test(h),
  },
  {
    platform: "bolt",
    confidence: 0.88,
    id: "marker:bolt",
    test: (h) => /bolt\.new|stackblitz\.com\/bolt|\.bolt\.host/i.test(h),
  },
  {
    platform: "beehiiv",
    confidence: 0.95,
    id: "cdn:beehiiv",
    test: (h) =>
      /media\.beehiiv\.com|beehiiv-images|subscribe-page\.beehiiv|beehiiv\.com\/_next|beehiiv-marketing/i.test(
        h,
      ),
  },
  {
    platform: "kit",
    confidence: 0.94,
    id: "cdn:kit",
    test: (h) =>
      /f\.convertkit\.com|convertkit\.com\/forms|ck\.page\/|kit\.com\/forms|ConvertKit\.forms/i.test(
        h,
      ),
  },
  {
    platform: "astro",
    confidence: 0.92,
    id: "meta:generator=Astro",
    test: (h) =>
      /name=["']generator["'][^>]*content=["'][^"']*Astro/i.test(h) ||
      /astro-island|data-astro-|astro\./i.test(h),
  },
  {
    platform: "nextjs",
    confidence: 0.9,
    id: "marker:__NEXT_DATA__",
    test: (h) => /__NEXT_DATA__|\/_next\/static\/|next\/dist\//i.test(h),
  },
  {
    platform: "nextjs",
    confidence: 0.82,
    id: "header:next",
    test: (h) => /x-powered-by:\s*next\.js|x-nextjs-/i.test(h),
  },
  {
    platform: "reactrouter",
    confidence: 0.78,
    id: "marker:react-router",
    test: (h) =>
      /__remixContext|remix-run|react-router|data-react-router/i.test(h),
  },
  {
    platform: "vuejs",
    confidence: 0.8,
    id: "marker:vue",
    test: (h) =>
      /__NUXT__|nuxt\.js|\/_nuxt\/|data-v-[a-f0-9]{8}[^>]*>|vue\.runtime\.esm/i.test(h),
  },
  {
    platform: "django",
    confidence: 0.78,
    id: "marker:django",
    test: (h) => /csrfmiddlewaretoken|django\.contrib|static\/admin\/css/i.test(h),
  },
  {
    platform: "laravel",
    confidence: 0.78,
    id: "marker:laravel",
    test: (h) => /laravel_session|XSRF-TOKEN.*laravel|\/vendor\/laravel/i.test(h),
  },
  {
    platform: "replit",
    confidence: 0.85,
    id: "marker:replit",
    test: (h) => /replit\.com|replit\.app|repl\.co/i.test(h),
  },
  {
    platform: "v0",
    confidence: 0.8,
    id: "marker:v0",
    test: (h) => /v0\.dev|vusercontent\.net/i.test(h),
  },
]

/**
 * Infer CMS / site builder from raw HTML (and optional response headers).
 * Shared fingerprinting core for:
 * - backend `POST /api/v1/detect-platform` (Firecrawl HTML)
 * - frontend interim scrape fallback
 */
export function inferPlatformFromHtml(
  html: string,
  headers?: Record<string, string> | Headers | null,
): PlatformInference {
  const headerText = (() => {
    if (!headers) return ""
    if (headers instanceof Headers) {
      return Array.from(headers.entries())
        .map(([k, v]) => `${k}: ${v}`)
        .join("\n")
    }
    return Object.entries(headers)
      .map(([k, v]) => `${k}: ${v}`)
      .join("\n")
  })()

  // Cap scan size — fingerprints live in <head> / early body / CDN URLs.
  const sample = `${headerText}\n${html.slice(0, 180_000)}`

  const matched: PlatformInference[] = []
  for (const rule of HTML_SIGNAL_RULES) {
    if (rule.test(sample)) {
      matched.push({
        platform: rule.platform,
        confidence: rule.confidence,
        signals: [rule.id],
      })
    }
  }

  if (!matched.length) {
    return { platform: "generic", confidence: 0.15, signals: [] }
  }

  // Aggregate by platform — multiple independent signals → higher confidence.
  const byPlatform = new Map<PlatformId, { confidence: number; signals: string[] }>()
  for (const m of matched) {
    const prev = byPlatform.get(m.platform)
    if (!prev) {
      byPlatform.set(m.platform, { confidence: m.confidence, signals: [...m.signals] })
      continue
    }
    prev.signals.push(...m.signals)
    // Each extra signal nudges confidence toward 0.99.
    prev.confidence = Math.min(0.99, Math.max(prev.confidence, m.confidence) + 0.02)
  }

  const ranked = [...byPlatform.entries()].sort(
    (a, b) => b[1].confidence - a[1].confidence,
  )
  const [platform, best] = ranked[0]

  return {
    platform,
    confidence: best.confidence,
    signals: [...new Set(best.signals)],
  }
}

/** Parse platform from a validate-setup / detect-platform API payload. */
export function parsePlatformFromApiPayload(payload: unknown): {
  platform: PlatformId
  confidence: number
  signals: string[]
  label?: string
} | null {
  if (!payload || typeof payload !== "object") return null
  const data = payload as Record<string, unknown>
  const nested = (key: string) => data[key] as Record<string, unknown> | undefined

  const candidates = [
    data.platform,
    data.detectedPlatform,
    data.sitePlatform,
    data.cms,
    data.siteType,
    data.builder,
    data.framework,
    data.stack,
    nested("result")?.platform,
    nested("result")?.cms,
    nested("detection")?.platform,
    nested("detection")?.cms,
    nested("data")?.platform,
    nested("branding")?.framework,
    nested("analysis")?.platform,
  ]

  let platform: PlatformId | null = null
  for (const candidate of candidates) {
    platform = normalizePlatformId(candidate)
    if (platform) break
  }

  // Some backends return `{ platforms: ["webflow"] }` or scored maps.
  if (!platform && Array.isArray(data.platforms)) {
    for (const item of data.platforms) {
      platform = normalizePlatformId(item)
      if (platform) break
    }
  }
  if (!platform && data.platforms && typeof data.platforms === "object") {
    const scored = Object.entries(data.platforms as Record<string, unknown>)
      .map(([key, value]) => ({
        platform: normalizePlatformId(key),
        score: typeof value === "number" ? value : 0,
      }))
      .filter((x): x is { platform: PlatformId; score: number } => !!x.platform)
      .sort((a, b) => b.score - a.score)
    if (scored[0]) platform = scored[0].platform
  }

  if (!platform) return null

  const confidenceRaw =
    data.confidence ??
    nested("detection")?.confidence ??
    nested("result")?.confidence ??
    nested("data")?.confidence ??
    data.score
  const confidence =
    typeof confidenceRaw === "number"
      ? Math.max(0, Math.min(1, confidenceRaw))
      : platform === "generic"
        ? 0.2
        : 0.8

  const signalsRaw =
    data.signals ?? nested("detection")?.signals ?? nested("result")?.signals
  const signals = Array.isArray(signalsRaw)
    ? signalsRaw.filter((s): s is string => typeof s === "string")
    : []

  const label =
    typeof data.label === "string"
      ? data.label
      : typeof data.platformLabel === "string"
        ? data.platformLabel
        : undefined

  return { platform, confidence, signals, label }
}

/** Pick the stronger of two inferences. Prefer a named platform on near-ties. */
export function preferStrongerInference(
  a: PlatformInference,
  b: PlatformInference,
): PlatformInference {
  if (b.confidence > a.confidence + 0.02) return b
  if (a.confidence > b.confidence + 0.02) return a
  if (a.platform === "generic" && b.platform !== "generic") return b
  if (b.platform === "generic" && a.platform !== "generic") return a
  // Same ballpark — more signals wins.
  if (b.signals.length > a.signals.length) return b
  if (a.signals.length > b.signals.length) return a
  return b.confidence >= a.confidence ? b : a
}
